import { CONTAINER_LOADED_EVENT, type ContainerLifecycleDetail } from '../parcels/types';
import { getHostFederationInstance } from './federation-runtime';

/**
 * Outcome of {@link ensureRemoteReady}. The primitive never throws — every
 * terminal state is reported through this union instead.
 *
 * - `ready`     — a usable container is on `window[scope]`; share scope is
 *                 initialized (unless opted out or the container is get-only).
 * - `failed`    — the host signalled an unrecoverable load failure via
 *                 `opts.failedFlag`, before or during the wait.
 * - `timeout`   — no usable container appeared within `timeoutMs`.
 * - `no-window` — running outside a browser (SSR / Node), nothing to wait for.
 */
export type RemoteReadyResult = 'ready' | 'failed' | 'timeout' | 'no-window';

export interface EnsureRemoteReadyOptions {
  /**
   * Max time to wait for a usable container before resolving `timeout`. Default 10000ms.
   * A non-positive value disables the timeout — the wait then settles only on container
   * arrival or `failedFlag` becoming truthy.
   */
  timeoutMs?: number;
  /** Poll interval while waiting for `window[scope]`. Default 50ms. */
  pollMs?: number;
  /**
   * Window flag the host sets when a remote failed to load (e.g.
   * `__ABAX_UI_LOAD_FAILED__`). When truthy — checked both before and during
   * the wait — resolve `failed` immediately instead of waiting out the timeout
   * on a remote that will never arrive.
   */
  failedFlag?: string;
  /**
   * Run the Module Federation share-scope init handshake once the container
   * is present. Default true. Set false when the caller performs its own
   * `container.init` (e.g. ParcelLoader, which initializes per mount).
   */
  initShareScope?: boolean;
}

/**
 * Minimal MF container shape. Every container exposes `get`; only webpack
 * containers expose `init` — non-webpack (Vite-shim) containers are get-only,
 * so `init` is optional. A container is usable as soon as `get` is present;
 * the share-scope handshake is conditional on `init` existing.
 */
interface MfContainer {
  get: unknown;
  init?: (shareScope: Record<string, unknown>) => Promise<void>;
}

function isUsableContainer(value: unknown): value is MfContainer {
  return !!value && typeof (value as MfContainer).get === 'function';
}

/** Read `window[scope]` and return it only if it is a usable MF container. */
function readUsableContainer(win: Record<string, unknown>, scope: string): MfContainer | undefined {
  const value = win[scope];
  return isUsableContainer(value) ? value : undefined;
}

/**
 * Wait for `window[scope]` to become a usable Module Federation container, then
 * (optionally) run the share-scope init handshake. Generalizes the
 * container-wait used by ParcelLoader and mirrors the shell's import-time init
 * guard so any MFE can gate rendering on a remote being ready.
 *
 * Resolution sources, fastest first:
 *  - immediate check of `window[scope]`
 *  - `CONTAINER_LOADED_EVENT` listener (hosts that dispatch it)
 *  - polling fallback (hosts that install the container without an event)
 *
 * Never rejects — see {@link RemoteReadyResult}.
 */
export async function ensureRemoteReady(
  scope: string,
  opts: EnsureRemoteReadyOptions = {},
): Promise<RemoteReadyResult> {
  if (typeof window === 'undefined') return 'no-window';

  const { failedFlag, initShareScope = true } = opts;
  const timeoutMs = opts.timeoutMs ?? 10000;
  const pollMs = opts.pollMs ?? 50;

  const win = window as unknown as Record<string, unknown>;

  // Host already gave up on this remote — degrade rather than wait it out.
  if (failedFlag && win[failedFlag]) return 'failed';

  const container = await waitForUsableContainer(scope, { timeoutMs, pollMs, failedFlag });
  if (!container) {
    // The wait ended without a container: either the host flipped the failed
    // flag mid-wait (degrade now) or nothing arrived within the timeout.
    return failedFlag && win[failedFlag] ? 'failed' : 'timeout';
  }

  if (initShareScope) await runShareScopeInit(scope, container);

  return 'ready';
}

interface WaitOptions {
  /**
   * Milliseconds before the wait gives up and resolves `undefined`. A value of
   * `0` (or any non-positive number) disables the timeout: the wait then settles
   * only when a usable container appears or `failedFlag` is set. Callers that
   * want a bounded wait must pass a positive value.
   */
  timeoutMs: number;
  pollMs: number;
  failedFlag?: string;
}

/**
 * Resolve a usable container for `scope`, or `undefined` on timeout / host
 * give-up. Uses both the lifecycle event (fast path) and a poll (fallback),
 * and observes `failedFlag` throughout so a late failure stops the wait
 * immediately. Cleans up the listener and interval on every settle path so
 * nothing leaks.
 */
function waitForUsableContainer(
  scope: string,
  { timeoutMs, pollMs, failedFlag }: WaitOptions,
): Promise<MfContainer | undefined> {
  const win = window as unknown as Record<string, unknown>;
  const immediate = readUsableContainer(win, scope);
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    let settled = false;
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    function cleanup() {
      if (pollId !== undefined) clearInterval(pollId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      window.removeEventListener(CONTAINER_LOADED_EVENT, onLoaded);
    }

    function settle(result: MfContainer | undefined) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    }

    // Returns true once the wait has been settled (container found or host
    // gave up), so callers can stop probing.
    function check(): boolean {
      if (failedFlag && win[failedFlag]) {
        settle(undefined);
        return true;
      }
      const container = readUsableContainer(win, scope);
      if (container) {
        settle(container);
        return true;
      }
      return false;
    }

    function onLoaded(event: Event) {
      const detail = (event as CustomEvent<ContainerLifecycleDetail>).detail;
      if (detail?.scope !== scope) return;
      check();
    }

    window.addEventListener(CONTAINER_LOADED_EVENT, onLoaded);
    pollId = setInterval(check, pollMs);

    if (timeoutMs > 0) {
      timeoutId = setTimeout(() => settle(undefined), timeoutMs);
    }
  });
}

/**
 * Run the webpack share-scope init handshake for a container. Mirrors the
 * shell's import-time guard. Never throws: the whole handshake is wrapped, so a
 * second init() for the same scope ("already initialized" — an expected race)
 * is swallowed, and any other failure is warned and left for the eventual
 * `container.get` to surface. Get-only (non-webpack) containers have no init()
 * and skip the container step.
 *
 * `__webpack_init_sharing__` / `__webpack_share_scopes__` are bare identifiers
 * webpack rewrites per-bundle; they cannot be reached through `window`.
 */
async function runShareScopeInit(scope: string, container: MfContainer): Promise<void> {
  // When an MF2 runtime host instance is present it owns share negotiation through
  // its own registry (`__FEDERATION__.__SHARE__`) and `loadRemote` has already
  // initialized the container against that scope. The classic handshake below reads
  // the bare `__webpack_share_scopes__`, which the runtime never populates — running
  // it would re-init the container against an empty/foreign scope. Skip it and let
  // the runtime own the scope. The classic path stays for a legacy host that has no
  // runtime instance (the transition fallback, retired in the MF2 endgame).
  if (getHostFederationInstance()) return;

  try {
    // eslint-disable-next-line no-undef
    await __webpack_init_sharing__('default');
    if (typeof container.init === 'function') {
      // eslint-disable-next-line no-undef
      await container.init(__webpack_share_scopes__.default);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // Webpack's actual container.init() wording is "already been initialized"
    // (see webpack's ContainerPlugin sharing runtime) — match that, not just
    // "already initialized", or the expected-race case never matches and warns.
    if (!/already (?:been )?initialized/i.test(message)) {
      // eslint-disable-next-line no-console
      console.warn(`[ensureRemoteReady] share-scope init failed for scope "${scope}":`, error);
    }
  }
}
